import { md5 } from 'js-md5'
import { sha256 } from 'js-sha256'

const CDN_AUTH_KEY_MAP: Record<string, string> = {
  'image.ytaxx.com': 'l6gDOMWe5hJuIg',
  'doc.ytaxx.com': 'SW5bwn2X2GlEGa6qvA',
  'audio.ytaxx.com': 'D05BVvbOHZ1qutQ9',
  'video.ytaxx.com': 'hFBgqMk60xfA5'
}

const TYPE_A_RANDOM_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

export type TencentCdnHashAlgorithm = 'md5' | 'sha256'

function createRandomString(length = 16) {
  let result = ''
  for (let i = 0; i < length; i++) {
    result += TYPE_A_RANDOM_CHARS[Math.floor(Math.random() * TYPE_A_RANDOM_CHARS.length)]
  }
  return result
}

/**
 * 生成腾讯云 CDN Type-A（时间戳防盗链）鉴权 URL。
 * 签名规则：md5/sha256(pathname-timestamp-rand-uid-key)，拼到 ?sign= 参数上。
 * 注意：URL 实际有效期由 CDN 控制台「鉴权 URL 有效时长」决定，此处只生成签名。
 * @param url 资源完整地址（不支持已带查询参数的 URL）
 * @param key CDN 控制台配置的鉴权密钥
 * @param algorithm 签名算法，需与 CDN 控制台一致，默认 sha256
 */
export function createTencentCdnTypeAAuthUrl(
  url: string,
  key: string,
  algorithm: TencentCdnHashAlgorithm = 'sha256'
): string {
  const [urlWithoutHash, hash = ''] = url.split('#')
  if (!urlWithoutHash || urlWithoutHash.includes('?')) {
    throw new Error('腾讯云 CDN Type-A 鉴权 URL 不能为空且不能带查询参数')
  }

  const protocolIndex = urlWithoutHash.indexOf('//')
  const pathIndex = urlWithoutHash.indexOf('/', protocolIndex + 2)
  const pathname = pathIndex === -1 ? '/' : urlWithoutHash.slice(pathIndex)

  const timestamp = Math.floor(Date.now() / 1000)
  const rand = createRandomString()
  const uid = 0
  const rawSign = `${pathname}-${timestamp}-${rand}-${uid}-${key}`
  const hashValue = algorithm === 'sha256' ? sha256(rawSign) : md5(rawSign)

  const authUrl = `${urlWithoutHash}?sign=${timestamp}-${rand}-${uid}-${hashValue}`
  return hash ? `${authUrl}#${hash}` : authUrl
}

/**
 * 尝试将本地文件地址转云端网络地址。
 */
export function tryConvertLocalFileNetworkURL(path?: string) {
  let url = path
  if (url && url.startsWith('http://')) {
    url = url.replace(/^http:\/\//, 'https://')
  }
  return url
}

function getCdnAuthHost(url: string) {
  const matchResult = url.match(/^https?:\/\/([^/?#]+)/i)
  return matchResult?.[1]?.toLowerCase() ?? ''
}

export function tryGetCdnAuthUrl(url?: string, key?: string): string | undefined {
  if (url && url.startsWith('http')) {
    const host = getCdnAuthHost(url)
    const authKey = key ?? CDN_AUTH_KEY_MAP[host]
    if (!authKey) {
      return url
    }

    try {
      return createTencentCdnTypeAAuthUrl(url, authKey)
    } catch (error) {
      console.warn('获取腾讯云 CDN 授权 URL 失败，使用原始 URL', error)
      return url
    }
  }
  return url
}

/**
 * 将本地/原始资源地址转成可直接访问的 CDN 鉴权地址。
 */
export function tryGetAuthUrl(path?: string, key?: string) {
  return tryGetCdnAuthUrl(tryConvertLocalFileNetworkURL(path), key)
}
